import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import {
  Announcement,
  AnnouncementStatus,
  AnnouncementGate,
  AnnouncementAccessEntry,
  fetchAnnouncements,
  deleteAnnouncement,
  fetchAnnouncementGate,
  setAnnouncementGate,
  fetchAnnouncementAccess,
  addAnnouncementAccess,
  removeAnnouncementAccess,
  resetUserAnnouncementData,
  ANNOUNCEMENT_STATUSES,
  ANNOUNCEMENT_TYPES,
} from '../services/announcementApi';

type TabType = 'announcements' | 'gate';

const STATUS_COLORS: Record<AnnouncementStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  ACTIVE: 'bg-green-100 text-green-700',
  ARCHIVED: 'bg-red-100 text-red-700',
};

const Announcements: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('announcements');

  // Announcements tab state
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  // Gate tab state
  const [gate, setGate] = useState<AnnouncementGate | null>(null);
  const [gateLoading, setGateLoading] = useState(false);
  const [gateToggling, setGateToggling] = useState(false);
  const [accessList, setAccessList] = useState<AnnouncementAccessEntry[]>([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newNote, setNewNote] = useState('');
  const [addingAccess, setAddingAccess] = useState(false);
  const [removingAccess, setRemovingAccess] = useState('');
  const [resettingAccess, setResettingAccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchAnnouncements({
        status: statusFilter || undefined,
        type: typeFilter || undefined,
        page,
        limit: 20,
      });
      setAnnouncements(res.data);
      setTotal(res.total);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load announcements');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, typeFilter]);

  useEffect(() => { load(); }, [load]);

  const loadGate = useCallback(async () => {
    setGateLoading(true);
    try {
      const [g, list] = await Promise.all([fetchAnnouncementGate(), fetchAnnouncementAccess()]);
      setGate(g);
      setAccessList(list);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load gate');
    } finally {
      setGateLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'gate') loadGate();
  }, [activeTab, loadGate]);

  const handleArchive = async (id: string, title: string) => {
    if (!window.confirm(`Archive "${title}"? It will stop showing to users.`)) return;
    try {
      await deleteAnnouncement(id);
      toast.success('Announcement archived');
      load();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleToggleGate = async () => {
    if (!gate) return;
    const next = !gate.enabled;
    if (next && !window.confirm('Enable announcements for ALL users? Remove the gate entirely.')) return;
    setGateToggling(true);
    try {
      const updated = await setAnnouncementGate(next);
      setGate(updated);
      toast.success(next ? 'Gate lifted — announcements visible to all users' : 'Gate enabled — whitelist only');
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setGateToggling(false);
    }
  };

  const handleAddAccess = async () => {
    const username = newUsername.trim();
    if (!username) return;
    setAddingAccess(true);
    try {
      const entry = await addAnnouncementAccess(username, newNote.trim() || undefined);
      setAccessList((prev) => [entry, ...prev]);
      setNewUsername('');
      setNewNote('');
      toast.success(`@${username} added to access list`);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setAddingAccess(false);
    }
  };

  const handleRemoveAccess = async (username: string) => {
    if (!window.confirm(`Remove @${username} from the access list?`)) return;
    setRemovingAccess(username);
    try {
      await removeAnnouncementAccess(username);
      setAccessList((prev) => prev.filter((e) => e.username !== username));
      toast.success(`@${username} removed`);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setRemovingAccess('');
    }
  };

  const handleResetAccess = async (userId: string, username: string) => {
    if (!window.confirm(`Reset ALL announcement data for @${username}?\n\nThis clears:\n• Announcement states\n• Announcement entries\n• Milestone unlocks\n• Gift events\n• Pre-entry view (so the full pre-entry flow shows again)\n• Owned event gifts (Collection tab)\n\nThis cannot be undone.`)) return;
    setResettingAccess(userId);
    try {
      const result = await resetUserAnnouncementData(userId);
      const { deleted } = result;
      toast.success(
        `@${username} reset — states: ${deleted.announcementStates}, entries: ${deleted.announcementEntries}, milestones: ${deleted.milestoneUnlocks}, gifts: ${deleted.walletGiftEvents}, preEntry: ${deleted.preEntryViews}, ownedGifts: ${deleted.ownedEventGifts}`
      );
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setResettingAccess('');
    }
  };

  const loadAccessList = async () => {
    setAccessLoading(true);
    try {
      const list = await fetchAnnouncementAccess();
      setAccessList(list);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setAccessLoading(false);
    }
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Announcements & Events</h1>
            <p className="text-sm text-gray-500 mt-1">SDUI-powered campaigns that render natively in the app</p>
          </div>
          {activeTab === 'announcements' && (
            <button
              onClick={() => navigate('/announcements/new')}
              className="px-4 py-2 bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white text-sm font-semibold rounded-lg hover:opacity-90 transition"
            >
              + New Announcement
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b">
          {(['announcements', 'gate'] as TabType[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium capitalize transition border-b-2 -mb-px ${
                activeTab === tab
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'gate' ? 'Access Gate' : 'Announcements'}
            </button>
          ))}
        </div>

        {/* ── Announcements Tab ── */}
        {activeTab === 'announcements' && (
          <>
            {/* Filters */}
            <div className="flex gap-3 mb-4">
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                className="px-3 py-2 border rounded-lg text-sm bg-white"
              >
                <option value="">All Statuses</option>
                {ANNOUNCEMENT_STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <select
                value={typeFilter}
                onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
                className="px-3 py-2 border rounded-lg text-sm bg-white"
              >
                <option value="">All Types</option>
                {ANNOUNCEMENT_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <span className="ml-auto text-sm text-gray-500 self-center">{total} total</span>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              {loading ? (
                <div className="p-12 text-center text-gray-400">Loading...</div>
              ) : announcements.length === 0 ? (
                <div className="p-12 text-center text-gray-400">No announcements found</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Announcement</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Type</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Audience</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Priority</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Start</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Expires</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Stats</th>
                      <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {announcements.map((a) => (
                      <tr key={a._id} className="border-b hover:bg-gray-50 transition">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <img
                              src={a.thumbnailUrl}
                              alt=""
                              className="w-10 h-10 rounded-lg object-cover bg-gray-200"
                              onError={(e) => { (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40"><rect fill="%23ddd" width="40" height="40"/></svg>'; }}
                            />
                            <div>
                              <div className="font-medium text-gray-900">{a.title}</div>
                              <div className="text-xs text-gray-400">/{a.slug}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full font-medium">{a.type}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${STATUS_COLORS[a.status]}`}>{a.status}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{a.targetAudience}</td>
                        <td className="px-4 py-3 text-gray-600">{a.priority}</td>
                        <td className="px-4 py-3 text-gray-600 text-xs">{new Date(a.startAt).toLocaleDateString()}</td>
                        <td className="px-4 py-3 text-gray-600 text-xs">
                          {a.expiresAt ? new Date(a.expiresAt).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          {a.stats ? `${a.stats.views} views` : '—'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => navigate(`/announcements/${a._id}/edit`)}
                              className="px-3 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition"
                            >
                              Edit
                            </button>
                            {a.status !== 'ARCHIVED' && (
                              <button
                                onClick={() => handleArchive(a._id, a.title)}
                                className="px-3 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg transition"
                              >
                                Archive
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-4">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-40 hover:bg-gray-100"
                >
                  Prev
                </button>
                <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-40 hover:bg-gray-100"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {/* ── Gate Tab ── */}
        {activeTab === 'gate' && (
          <div className="space-y-6">
            {/* Gate status card */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Announcement Visibility Gate</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    When disabled, only whitelisted users below can see announcements in the app.
                    All others see an empty list silently.
                  </p>
                </div>
                {gateLoading ? (
                  <div className="text-sm text-gray-400">Loading...</div>
                ) : gate ? (
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${gate.enabled ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                      {gate.enabled ? 'OPEN — All users' : 'GATED — Whitelist only'}
                    </span>
                    <button
                      onClick={handleToggleGate}
                      disabled={gateToggling}
                      className={`px-4 py-2 text-sm font-semibold rounded-lg transition disabled:opacity-50 ${
                        gate.enabled
                          ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {gateToggling ? '...' : gate.enabled ? 'Enable Gate' : 'Lift Gate for All'}
                    </button>
                  </div>
                ) : null}
              </div>
            </div>

            {/* Add user */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">Add User to Whitelist</h2>
              <div className="flex gap-3">
                <input
                  type="text"
                  placeholder="@username"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddAccess()}
                  className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
                <input
                  type="text"
                  placeholder="Note (optional)"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddAccess()}
                  className="w-48 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                />
                <button
                  onClick={handleAddAccess}
                  disabled={addingAccess || !newUsername.trim()}
                  className="px-4 py-2 bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white text-sm font-semibold rounded-lg hover:opacity-90 transition disabled:opacity-50"
                >
                  {addingAccess ? 'Adding...' : 'Add'}
                </button>
              </div>
            </div>

            {/* Whitelist table */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b">
                <h2 className="text-base font-semibold text-gray-900">
                  Whitelisted Users <span className="text-gray-400 font-normal text-sm">({accessList.length})</span>
                </h2>
                <button
                  onClick={loadAccessList}
                  disabled={accessLoading}
                  className="text-sm text-gray-500 hover:text-gray-700 transition"
                >
                  {accessLoading ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>

              {accessList.length === 0 ? (
                <div className="p-12 text-center text-gray-400">No users in the whitelist yet</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Username</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">User ID</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Note</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Added By</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Added At</th>
                      <th className="text-right px-4 py-3 font-medium text-gray-600">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accessList.map((entry) => (
                      <tr key={entry._id} className="border-b hover:bg-gray-50 transition">
                        <td className="px-4 py-3 font-medium text-gray-900">@{entry.username}</td>
                        <td className="px-4 py-3 text-xs text-gray-400 font-mono">{entry.userId}</td>
                        <td className="px-4 py-3 text-gray-600">{entry.note || '—'}</td>
                        <td className="px-4 py-3 text-xs text-gray-500 font-mono">{entry.addedBy}</td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          {new Date(entry.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleResetAccess(entry.userId, entry.username)}
                              disabled={resettingAccess === entry.userId || removingAccess === entry.username}
                              className="px-3 py-1 text-xs font-medium text-orange-600 hover:bg-orange-50 rounded-lg transition disabled:opacity-50"
                            >
                              {resettingAccess === entry.userId ? 'Resetting...' : 'Reset'}
                            </button>
                            <button
                              onClick={() => handleRemoveAccess(entry.username)}
                              disabled={removingAccess === entry.username || resettingAccess === entry.userId}
                              className="px-3 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50"
                            >
                              {removingAccess === entry.username ? 'Removing...' : 'Remove'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Announcements;
